import React from "react";

import ResolutionRateFormula from "@common/assets/images/releases/resolution_rate_ratio_formula.png";
import Button from "@common/components/Button/Button";
import Heading from "@common/components/Heading/Heading";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { Spinner } from "@common/components/Spinner/Spinner";
import { TicketPriority } from "@common/models/TicketPriority.model";
import { getPriorityIcon } from "@common/util/getIconsHelper";
import useResolutionRateScoring from "@main/pages/Release/consumers/useResolutionRateScoring";
import { MetricScoreOutletContext } from "@main/pages/Release/views/MetricDetails/ReleaseMetricDetailsLayout";
import { PriorityRow } from "@main/pages/Release/views/MetricDetails/ResolutionRate/PriorityRow";
import { MetricRow } from "@main/pages/Release/views/MetricDetails/shared/MetricRow";
import * as MetricDetailsStyled from "main/pages/Release/views/MetricDetails/MetricDetails.styles";
import { useOutletContext } from "react-router-dom";

import * as Styled from "./ResolutionRateScoreView.styles";

export default function ResolutionRateScoreView() {
  const { refetchReleaseStatus } = useOutletContext<MetricScoreOutletContext>();
  const {
    thresholdRows,
    weightHigh,
    setWeightHigh,
    weightMedium,
    setWeightMedium,
    weightLow,
    setWeightLow,
    scoringConfigLoading,
    handleSave,
    isSaving,
  } = useResolutionRateScoring(refetchReleaseStatus);

  if (scoringConfigLoading) {
    return <Spinner />;
  }

  const hasThresholdError = thresholdRows.some((t) => Boolean(t.error));

  return (
    <div style={{ padding: "3.2rem 0" }}>
      <MetricDetailsStyled.MetricsCard>
        <Heading
          heading3
          marginBottom={24}
        >
          Score
        </Heading>
        <Heading
          heading4
          fontWeight={"bold"}
          marginBottom={16}
        >
          Calculating the ratio
        </Heading>

        <Paragraph marginBottom={16}>
          {
            "The ratio compares closed defect points against newly created ones. Each defect's points depend on its priority, configurable below:"
          }
        </Paragraph>

        <img
          style={{ marginBottom: "1.6rem" }}
          width={360}
          src={ResolutionRateFormula}
          alt="Formula"
        />

        <Styled.ThresholdList>
          <PriorityRow
            icon={getPriorityIcon(TicketPriority.HIGH)}
            label="High priority weight"
            value={weightHigh}
            onChange={setWeightHigh}
            description="Each 'high priority' defect carries this many points.   ←   System default: 5."
          />
          <PriorityRow
            icon={getPriorityIcon(TicketPriority.MEDIUM)}
            label="Medium priority weight"
            value={weightMedium}
            onChange={setWeightMedium}
            description="Each 'medium priority' defect carries this many points.   ←   System default: 3."
          />
          <PriorityRow
            icon={getPriorityIcon(TicketPriority.LOW)}
            label="Low priority weight"
            value={weightLow}
            onChange={setWeightLow}
            description="Each 'low priority' defect carries this many points.   ←   System default: 1."
          />
        </Styled.ThresholdList>

        <Heading
          heading4
          fontWeight={"bold"}
          marginBottom={16}
        >
          Mapping
        </Heading>

        <Paragraph>
          {"The 'Resolution rate' is then mapped to a score based on the thresholds below.\n" +
            "Within each range, the score interpolates linearly between adjacent thresholds. A value exactly halfway between two thresholds gives a score halfway between their assigned scores.\n" +
            "Higher 'Resolution rate' indicates healthier defect management. A 'Resolution rate' of 100% means you close as many defects as you create, while 200% means you close twice as many as you create."}
        </Paragraph>
        <Styled.ThresholdList>
          {thresholdRows.map((row) => (
            <MetricRow
              key={row.score}
              description={row.description}
              value={row.value}
              error={row.error}
              onChange={row.onChange}
              score={row.score}
              unit={"%"}
              showScore
            />
          ))}
        </Styled.ThresholdList>

        <Button
          type="submit"
          size="small"
          disabled={hasThresholdError || isSaving}
          onClick={handleSave}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </MetricDetailsStyled.MetricsCard>
    </div>
  );
}
